import { useEffect, useMemo, useRef, useState } from "react";
import { Quote, Copy, Check, Loader2, QrCode, Download } from "lucide-react";
import { toast } from "sonner";
import { QRCodeSVG, QRCodeCanvas } from "qrcode.react";
import InstagramCardButton from "@/components/InstagramCardButton";
import { Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { client } from "@/lib/api";
import { trackEvent } from "@/lib/ga4";

/**
 * CitationBlock — v3.36
 *
 * Renders 3 copy-to-clipboard buttons (ABNT / APA / BibTeX) that pull
 * pre-built citations from the backend, plus a discreet QR-code icon
 * that pops a 150-px QR for the canonical page URL — handy for slide
 * decks, bibliographies, and classroom projection.
 *
 * Props:
 *   - endpoint: string  -- absolute API path that returns
 *     { citations: { abnt, apa, bibtex } }
 *   - testidPrefix: string  -- distinguishes "video" vs "article"
 *     for e2e tests (e.g. `citation-video` or `citation-article`)
 *   - kindLabel: string -- "vídeo" or "artigo" for the section title
 *   - shareUrl: string  -- optional canonical URL to encode in the QR.
 *     Falls back to window.location.href on the client.
 */
export default function CitationBlock({
  endpoint,
  testidPrefix = "citation",
  kindLabel = "vídeo",
  shareUrl,
  cardData,
}) {
  const [data, setData] = useState(null); // { abnt, apa, bibtex }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(null); // 'abnt' | 'apa' | 'bibtex'

  const qrUrl = useMemo(() => {
    if (shareUrl) return shareUrl;
    if (typeof window !== "undefined") return window.location.href;
    return "";
  }, [shareUrl]);

  // Offscreen high-resolution canvas used solely to export a PNG when the
  // user clicks "Baixar QR Code". Rendering it inside the popover keeps the
  // download instant — no need to spin up a new canvas at click time.
  const downloadCanvasRef = useRef(null);

  const safeKind = useMemo(() => {
    const base = (kindLabel || "midia")
      .toLowerCase()
      .normalize("NFKD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
    return base || "midia";
  }, [kindLabel]);

  const downloadQr = () => {
    const wrapper = downloadCanvasRef.current;
    const canvas = wrapper?.querySelector("canvas");
    if (!canvas) {
      toast.error("Não foi possível gerar o PNG. Recarregue a página e tente de novo.");
      return;
    }
    try {
      const dataUrl = canvas.toDataURL("image/png");
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = `juristube-qr-${safeKind}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success("QR Code salvo na pasta de downloads.");
    } catch (e) {
      toast.error("Seu navegador bloqueou o download. Tente em outro navegador.");
    }
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    client
      .get(endpoint)
      .then((r) => {
        if (!cancelled) setData(r.data?.citations || null);
      })
      .catch((e) => {
        if (!cancelled)
          setError(e?.response?.data?.detail || e?.message || "Falha");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [endpoint]);

  const doCopy = async (format) => {
    const text = data?.[format];
    if (!text) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.setAttribute("readonly", "");
        ta.style.position = "absolute";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopied(format);
      toast.success(`Citação ${LABEL[format]} copiada.`);
      trackEvent("cite_copy", {
        format,
        kind: kindLabel || "item",
        endpoint,
      });
      setTimeout(() => setCopied((c) => (c === format ? null : c)), 2200);
    } catch {
      toast.error("Não foi possível copiar. Tente selecionar manualmente.");
    }
  };

  return (
    <section
      className="rounded-2xl border border-amber-500/25 bg-slate-950/40 p-5 sm:p-6"
      data-testid={`${testidPrefix}-block`}
      aria-labelledby={`${testidPrefix}-heading`}
    >
      <header className="flex items-center gap-2 mb-3">
        <Quote className="w-4 h-4 text-amber-400 shrink-0" />
        <h2
          id={`${testidPrefix}-heading`}
          className="text-[11px] uppercase tracking-[0.18em] text-amber-300/90 font-semibold"
        >
          Citação acadêmica
        </h2>
      </header>

      <p className="text-[12px] text-slate-400 leading-relaxed mb-4">
        Copie a referência deste {kindLabel} no estilo da sua escolha e cole no
        seu trabalho. Geramos a citação no padrão exigido pela maioria das
        revistas jurídicas brasileiras.{" "}
        <a
          href="/como-citar"
          className="text-amber-300 hover:text-amber-200 underline decoration-dotted underline-offset-2 font-medium"
          data-testid={`${testidPrefix}-como-citar-link`}
        >
          Ver guia completo
        </a>
        .
      </p>

      {loading && (
        <div className="flex items-center gap-2 text-slate-400 text-sm py-3">
          <Loader2 className="w-4 h-4 animate-spin" /> Gerando referências…
        </div>
      )}

      {error && !loading && (
        <p className="text-sm text-red-300/90 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">
          Erro ao gerar a citação: {error}
        </p>
      )}

      {data && !loading && !error && (
        <div className="space-y-3">
          {/* Buttons row — sized so all 4 fit on mobile too. */}
          <div className="flex flex-wrap items-center gap-2">
            {["abnt", "apa", "chicago", "bibtex"].map((fmt) => (
              <button
                key={fmt}
                type="button"
                onClick={() => doCopy(fmt)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-amber-500/40 bg-amber-500/[0.08] hover:bg-amber-500/[0.16] text-amber-100 text-xs font-semibold transition"
                data-testid={`${testidPrefix}-copy-${fmt}`}
              >
                {copied === fmt ? (
                  <Check className="w-3.5 h-3.5 text-emerald-300" />
                ) : (
                  <Copy className="w-3.5 h-3.5" />
                )}
                Copiar {LABEL[fmt]}
              </button>
            ))}

            {/* Discreet QR-code icon. Opens a popover with a scannable
                 code sized for slide projection (150px). */}
            {qrUrl && (
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    aria-label="Mostrar QR Code da página"
                    title="QR Code para slides / bibliografias"
                    className="inline-flex items-center justify-center w-8 h-8 rounded-full border border-amber-500/40 bg-amber-500/[0.08] hover:bg-amber-500/[0.20] text-amber-200 transition"
                    data-testid={`${testidPrefix}-qr-trigger`}
                  >
                    <QrCode className="w-4 h-4" />
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  align="start"
                  sideOffset={8}
                  className="w-auto p-4 bg-slate-950 border-amber-500/30 text-slate-200"
                  data-testid={`${testidPrefix}-qr-popover`}
                >
                  <div className="flex flex-col items-center gap-3">
                    <div className="bg-white p-3 rounded-lg">
                      <QRCodeSVG
                        value={qrUrl}
                        size={150}
                        level="M"
                        marginSize={0}
                        data-testid={`${testidPrefix}-qr-svg`}
                      />
                    </div>
                    <p className="text-[11px] leading-snug text-slate-300 max-w-[180px] text-center">
                      Escaneie para compartilhar este {kindLabel} em slides ou
                      bibliografias.
                    </p>
                    <button
                      type="button"
                      onClick={downloadQr}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-500 hover:bg-amber-400 text-slate-950 text-xs font-semibold transition"
                      data-testid={`${testidPrefix}-qr-download`}
                    >
                      <Download className="w-3.5 h-3.5" />
                      Baixar QR Code (PNG)
                    </button>

                    {cardData && (
                      <InstagramCardButton
                        cardData={cardData}
                        qrUrl={qrUrl}
                        kindLabel={kindLabel}
                        variant="pill"
                        testid={`${testidPrefix}-card-download`}
                      />
                    )}

                    <p className="text-[10px] leading-snug text-slate-500 max-w-[180px] text-center">
                      Salve a imagem e use livremente em posts no Instagram,
                      LinkedIn ou slides de aula.
                    </p>
                  </div>

                  {/* Offscreen high-resolution canvas used as the source for
                       the PNG download. Sized at 1024 px so the saved file is
                       crisp on retina screens and large social-media posts. */}
                  <div
                    ref={downloadCanvasRef}
                    aria-hidden="true"
                    style={{
                      position: "absolute",
                      left: "-9999px",
                      top: "-9999px",
                      pointerEvents: "none",
                    }}
                  >
                    <QRCodeCanvas
                      value={qrUrl}
                      size={1024}
                      level="M"
                      marginSize={2}
                      bgColor="#ffffff"
                      fgColor="#000000"
                    />
                  </div>
                </PopoverContent>
              </Popover>
            )}
          </div>

          {/* Preview ABNT inline — already the most-used format here. */}
          <details
            className="group"
            data-testid={`${testidPrefix}-preview-details`}
          >
            <summary className="cursor-pointer text-xs text-slate-400 hover:text-slate-200 inline-flex items-center gap-1">
              Ver prévia das três referências
              <span className="text-slate-600 group-open:hidden">▸</span>
              <span className="text-slate-600 hidden group-open:inline">▾</span>
            </summary>
            <div className="mt-3 space-y-3">
              <Block
                label="ABNT"
                value={data.abnt}
                testid={`${testidPrefix}-preview-abnt`}
              />
              <Block
                label="APA"
                value={data.apa}
                testid={`${testidPrefix}-preview-apa`}
              />
              <Block
                label="Chicago"
                value={data.chicago}
                testid={`${testidPrefix}-preview-chicago`}
              />
              <Block
                label="BibTeX"
                value={data.bibtex}
                mono
                testid={`${testidPrefix}-preview-bibtex`}
              />
            </div>
          </details>
        </div>
      )}
    </section>
  );
}

const LABEL = { abnt: "ABNT", apa: "APA", chicago: "Chicago", bibtex: "BibTeX" };

function Block({ label, value, mono, testid }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">
        {label}
      </div>
      <pre
        className={`whitespace-pre-wrap break-words rounded-lg bg-slate-900/70 border border-slate-800 px-3 py-2 text-[12px] leading-relaxed text-slate-200 ${
          mono ? "font-mono" : "font-serif"
        }`}
        data-testid={testid}
      >
        {value}
      </pre>
    </div>
  );
}
